import React, { useEffect, useState } from "react";
import { getCurrentServerInstant, getServerTimezone } from "@/shared/utils/serverTimeContext";
import Tooltip from "@/shared/components/Tooltip";

/**
 * Displays the current time in the authoritative server timezone using the
 * bootstrapped server instant when available, then advances locally from that
 * baseline. Mirrors RoR header intent without relying on browser local time.
 *
 * Two render modes that swap on viewport size:
 *   • lg+ (≥ 1024 px): full label "<weekday> <day> <month> HH:MM <TZ>"
 *   • below lg:        compact pill — "HH:MM" digital readout
 *
 * History:
 *   • 2026-05-11 (i): removed inner Sun/Moon glyph — collided with the
 *     AppearanceToggle's theme Sun/Moon (users instinctively clicked the
 *     clock's moon to toggle dark mode).
 *   • 2026-05-11 (ii): removed the surrounding day-progress ring. The
 *     ring was originally paired with the celestial glyph; once that
 *     glyph went, the ring became decorative overhead with no payload —
 *     the digital readout already tells the user the time. Day/night
 *     state moved to an ambient navbar background tint (see
 *     `useServerPhase` and `Navbar.css`) — same signal, zero icon
 *     surface, lower confusion risk.
 */
const timeFormatterCache = new Map<string, Intl.DateTimeFormat>();
const tooltipFormatterCache = new Map<string, Intl.DateTimeFormat>();
const hhmmFormatterCache = new Map<string, Intl.DateTimeFormat>();

const getTimeFormatter = (timeZone: string): Intl.DateTimeFormat => {
  const cached = timeFormatterCache.get(timeZone);
  if (cached) return cached;

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZoneName: "short",
  });
  timeFormatterCache.set(timeZone, formatter);
  return formatter;
};

const getTooltipFormatter = (timeZone: string): Intl.DateTimeFormat => {
  const cached = tooltipFormatterCache.get(timeZone);
  if (cached) return cached;

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "long",
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
  tooltipFormatterCache.set(timeZone, formatter);
  return formatter;
};

/** Cached "HH:MM" formatter (24-hour) for the compact mobile pill. */
const getHhMmFormatter = (timeZone: string): Intl.DateTimeFormat => {
  const cached = hhmmFormatterCache.get(timeZone);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  hhmmFormatterCache.set(timeZone, formatter);
  return formatter;
};

const getFormattedServerTime = (): string => {
  const formatter = getTimeFormatter(getServerTimezone());
  const parts = formatter.formatToParts(getCurrentServerInstant());
  const weekday = parts.find((p) => p.type === "weekday")?.value ?? "";
  const day = parts.find((p) => p.type === "day")?.value ?? "00";
  const month = parts.find((p) => p.type === "month")?.value ?? "";
  const hour = parts.find((p) => p.type === "hour")?.value ?? "00";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "00";
  const zone = parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  return `${weekday} ${day} ${month} ${hour}:${minute} ${zone}`.trim();
};

/** Just "HH:MM" in the server timezone — for the compact pill. */
const getFormattedHhMm = (): string => {
  const parts = getHhMmFormatter(getServerTimezone()).formatToParts(
    getCurrentServerInstant(),
  );
  const hour = parts.find((p) => p.type === "hour")?.value ?? "00";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "00";
  return `${hour}:${minute}`;
};

const getServerTooltipText = (timeZone: string): string => {
  const tooltipDateTime = getTooltipFormatter(timeZone).format(getCurrentServerInstant());
  return `Server time: ${tooltipDateTime} (${timeZone}). All time-based filters use server time.`;
};

const CurrentTime: React.FC = () => {
  const [currentTime, setCurrentTime] = useState<string>(getFormattedServerTime);
  const [hhmm, setHhmm] = useState<string>(getFormattedHhMm);
  const [serverTimezone, setServerTimezone] = useState<string>(getServerTimezone);

  useEffect(() => {
    const tick = (): void => {
      setCurrentTime((prev) => {
        const next = getFormattedServerTime();
        return prev === next ? prev : next;
      });
      setHhmm((prev) => {
        const next = getFormattedHhMm();
        return prev === next ? prev : next;
      });
      setServerTimezone((prev) => {
        const next = getServerTimezone();
        return prev === next ? prev : next;
      });
    };

    tick();
    const interval = setInterval(tick, 10_000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Tooltip
      text={getServerTooltipText(serverTimezone)}
      position="bottom"
      align="center"
    >
      <span
        className="text-on-primary text-sm whitespace-nowrap inline-flex items-center rounded-full border border-white/25 bg-white/10 px-2.5 py-1 lg:px-3"
        data-testid="navbar-current-time"
        aria-live="off"
      >
        {/* lg+: full label.  below lg: compact "HH:MM" digital readout. */}
        <span className="hidden lg:inline">{currentTime}</span>
        <span className="lg:hidden tabular-nums" aria-hidden={true}>{hhmm}</span>
        {/* Screen-reader full label at every viewport so AT users get
            weekday / month / timezone regardless of breakpoint. */}
        <span className="sr-only">{currentTime}</span>
      </span>
    </Tooltip>
  );
};

export default CurrentTime;
